import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getProfile, saveProfile, EMPTY_PROFILE } from '../services/profileService';
import { isPageBreakId } from '../utils/pageBreakUtils';

/**
 * The user's choices: favorites, the order they're shown in, custom links,
 * and games dismissed from Editor's Picks.
 *
 * Not the display name - `username` lives on the user document and is set by
 * signup or the invite flow, not here (D39).
 *
 * All of this was localStorage before D22 and is now server state. There is
 * no cache and no local mirror (D25) - the round trip on first paint is
 * accepted, and a cache would be a second source of truth.
 *
 * Every mutation is optimistic, and reverts on failure with an error set.
 * There is no retry queue (D10): a failed write simply didn't happen, and the
 * UI has to say so rather than appear to succeed and quietly forget.
 */
const PrefsContext = createContext();

export const PrefsProvider = ({ children }) => {
    const { user, isLoading: authLoading, refreshUser } = useAuth();

    const [prefs, setPrefs] = useState(EMPTY_PROFILE);
    const [prefsLoading, setPrefsLoading] = useState(true);
    const [prefsError, setPrefsError] = useState(null);

    // Whether a record existed last time we looked. A write can bring one into
    // being, and the rest of the app needs to hear about it.
    const hadUserRef = useRef(false);
    useEffect(() => { hadUserRef.current = !!user; }, [user]);

    useEffect(() => {
        if (authLoading) return;

        let cancelled = false;
        setPrefsLoading(true);

        getProfile()
            .then((profile) => {
                if (!cancelled) setPrefs(profile);
            })
            .catch((error) => {
                console.log('Error loading profile:', error);
                if (!cancelled) setPrefsError('Could not load your games. Please reload the page.');
            })
            .finally(() => {
                if (!cancelled) setPrefsLoading(false);
            });

        return () => { cancelled = true; };
    }, [authLoading, user?.id]);

    /**
     * Apply a change optimistically, persist it, and roll back if the write
     * fails. `changes` is per-field replace - fields left out are untouched
     * server-side, so two unrelated edits can't clobber each other.
     */
    const commit = useCallback(async (changes, failureMessage) => {
        const previous = prefs;
        setPrefs((current) => ({ ...current, ...changes }));
        setPrefsError(null);

        try {
            const saved = await saveProfile(changes);
            setPrefs(saved);

            // The first write may have created the account. Let AuthContext
            // notice, so anything keyed on having an identity updates.
            if (!hadUserRef.current) {
                hadUserRef.current = true;
                refreshUser().catch((error) => console.log('Error refreshing user after first write:', error));
            }

            return true;
        } catch (error) {
            console.log('Error saving profile:', error);
            setPrefs(previous);
            setPrefsError(failureMessage);
            return false;
        }
    }, [prefs, refreshUser]);

    // A page break's id is already the string form it's stored in - only a
    // real game id needs coercing from whatever a caller passed in.
    const addFavorite = useCallback((gameId) => {
        const id = isPageBreakId(gameId) ? gameId : Number(gameId);
        if (prefs.favorites.includes(id)) return Promise.resolve(true);
        return commit(
            { favorites: [...prefs.favorites, id] },
            "Couldn't save that to My Games. Please try again."
        );
    }, [commit, prefs.favorites]);

    const removeFavorite = useCallback((gameId) => {
        const id = isPageBreakId(gameId) ? gameId : Number(gameId);
        return commit(
            { favorites: prefs.favorites.filter((f) => f !== id) },
            "Couldn't remove that from My Games. Please try again."
        );
    }, [commit, prefs.favorites]);

    const unlikeGame = useCallback((gameId) => {
        const id = Number(gameId);
        if (prefs.unliked.includes(id)) return Promise.resolve(true);
        return commit(
            { unliked: [...prefs.unliked, id] },
            "Couldn't hide that game. Please try again."
        );
    }, [commit, prefs.unliked]);

    const addCustomLink = useCallback((link) => {
        return commit(
            { customLinks: [...prefs.customLinks, { ...link, isCustom: true }] },
            "Couldn't save your link. Please try again."
        );
    }, [commit, prefs.customLinks]);

    const removeCustomLink = useCallback((linkId) => {
        return commit(
            { customLinks: prefs.customLinks.filter((link) => link.id !== linkId) },
            "Couldn't remove your link. Please try again."
        );
    }, [commit, prefs.customLinks]);

    /**
     * Commit a reordering of My Games. Reordering is held in the component
     * while the user is dragging rows around and saved once, on "Done
     * editing" - not once per move.
     */
    const saveFavoriteOrder = useCallback((order) => {
        return commit(
            { favoriteOrder: order },
            "Couldn't save the new order. Please try again."
        );
    }, [commit]);

    const dismissPrefsError = useCallback(() => setPrefsError(null), []);

    return (
        <PrefsContext.Provider value={{
            favorites: prefs.favorites,
            favoriteOrder: prefs.favoriteOrder,
            customLinks: prefs.customLinks,
            unlikedGames: prefs.unliked,

            prefsLoading,
            prefsError,
            dismissPrefsError,

            addFavorite,
            removeFavorite,
            unlikeGame,
            addCustomLink,
            removeCustomLink,
            saveFavoriteOrder
        }}>
            {children}
        </PrefsContext.Provider>
    );
};

export const usePrefs = () => {
    const context = useContext(PrefsContext);
    if (!context) {
        throw new Error('usePrefs must be used within a PrefsProvider');
    }
    return context;
};
